import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  CalibrationPayload,
  CreateDevicePayload,
  DeviceItem,
  DeviceProfile,
  DeviceStatistics,
  EventListResponse,
  MetricListResponse,
  UpdateDevicePayload,
} from "entities/devices";

import { SilentMeta } from "store/silent";

export type UpdateDeviceActionPayload = {
  devEui: string;
  body: UpdateDevicePayload;
};

export type MarkCalibrationActionPayload = {
  devEui: string;
  values: CalibrationPayload;
};

type Meta = {
  total: number;
  page: number;
  page_size: number;
};

type DeviceState = {
  items: DeviceItem[];
  current: DeviceItem | null;
  isLoading: boolean;
  isCurrentLoading: boolean;
  meta: Meta;

  statistics: DeviceStatistics;
  isStatisticsLoading: boolean;

  profiles: DeviceProfile[];
  isProfilesLoading: boolean;

  metrics: MetricListResponse | null;
  isMetricsLoading: boolean;
  events: EventListResponse | null;
  isEventsLoading: boolean;

  isCreating: boolean;
  createError: string | null;
  lastCreatedDevEui: string | null;

  isUpdating: boolean;
  updateError: string | null;
  lastUpdatedDevEui: string | null;

  isRemoving: boolean;
  removeError: string | null;
  lastRemovedDevEui: string | null;

  isCalibrating: boolean;
  calibrateError: string | null;
  lastCalibratedDevEui: string | null;

  errors: { detail: unknown } | null;
};

const initialState: DeviceState = {
  items: [],
  current: null,
  isLoading: false,
  isCurrentLoading: false,
  meta: { total: 0, page: 1, page_size: 20 },

  statistics: { total: 0, online: 0, offline: 0, never_seen: 0 },
  isStatisticsLoading: false,

  profiles: [],
  isProfilesLoading: false,

  metrics: null,
  isMetricsLoading: false,
  events: null,
  isEventsLoading: false,

  isCreating: false,
  createError: null,
  lastCreatedDevEui: null,

  isUpdating: false,
  updateError: null,
  lastUpdatedDevEui: null,

  isRemoving: false,
  removeError: null,
  lastRemovedDevEui: null,

  isCalibrating: false,
  calibrateError: null,
  lastCalibratedDevEui: null,

  errors: null,
};

const deviceSlice = createSlice({
  name: "devices",
  initialState,
  reducers: {
    getDeviceListRequest: {
      reducer(state, action: PayloadAction<string, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isLoading = true;
      },
      prepare: (query = "", meta: SilentMeta = {}) => ({ payload: query, meta }),
    },
    getDeviceListSuccess(
      state,
      action: PayloadAction<{ items: DeviceItem[]; total: number; page: number; page_size: number }>,
    ) {
      state.items = action.payload.items;
      state.meta = {
        total: action.payload.total,
        page: action.payload.page,
        page_size: action.payload.page_size,
      };
      state.isLoading = false;
    },
    getDeviceListFailure(state) {
      state.isLoading = false;
    },

    getDeviceStatisticsRequest: {
      reducer(state, action: PayloadAction<string | undefined, string, SilentMeta>) {
        void action.payload;
        if (!action.meta?.silent) state.isStatisticsLoading = true;
      },
      prepare: (query?: string, meta: SilentMeta = {}) => ({ payload: query, meta }),
    },
    getDeviceStatisticsSuccess(state, action: PayloadAction<DeviceStatistics>) {
      state.statistics = action.payload;
      state.isStatisticsLoading = false;
    },
    getDeviceStatisticsFailure(state) {
      state.isStatisticsLoading = false;
    },

    getDeviceRequest(state, action: PayloadAction<string>) {
      void action.payload;
      state.isCurrentLoading = true;
      state.current = null;
    },
    getDeviceSuccess(state, action: PayloadAction<DeviceItem>) {
      state.current = action.payload;
      state.isCurrentLoading = false;
    },
    getDeviceFailure(state) {
      state.isCurrentLoading = false;
    },

    getDeviceProfilesRequest(state) {
      state.isProfilesLoading = true;
    },
    getDeviceProfilesSuccess(state, action: PayloadAction<DeviceProfile[]>) {
      state.profiles = action.payload;
      state.isProfilesLoading = false;
    },
    getDeviceProfilesFailure(state) {
      state.isProfilesLoading = false;
    },

    getDeviceMetricsRequest: {
      reducer(state, action: PayloadAction<{ devEui: string; query: string }>) {
        void action.payload;
        state.isMetricsLoading = true;
      },
      prepare: (devEui: string, query = "") => ({ payload: { devEui, query } }),
    },
    getDeviceMetricsSuccess(state, action: PayloadAction<MetricListResponse>) {
      state.metrics = action.payload;
      state.isMetricsLoading = false;
    },
    getDeviceMetricsFailure(state) {
      state.isMetricsLoading = false;
    },

    getDeviceEventsRequest: {
      reducer(state, action: PayloadAction<{ devEui: string; query: string }>) {
        void action.payload;
        state.isEventsLoading = true;
      },
      prepare: (devEui: string, query = "") => ({ payload: { devEui, query } }),
    },
    getDeviceEventsSuccess(state, action: PayloadAction<EventListResponse>) {
      state.events = action.payload;
      state.isEventsLoading = false;
    },
    getDeviceEventsFailure(state) {
      state.isEventsLoading = false;
    },

    createDeviceRequest(
      state,
      action: PayloadAction<{ payload: CreateDevicePayload; certificate: File | null }>,
    ) {
      void action.payload;
      state.isCreating = true;
      state.createError = null;
      state.lastCreatedDevEui = null;
    },
    createDeviceSuccess(state, action: PayloadAction<DeviceItem>) {
      state.isCreating = false;
      state.lastCreatedDevEui = action.payload.dev_eui;
    },
    createDeviceFailure(state, action: PayloadAction<string>) {
      state.isCreating = false;
      state.createError = action.payload;
    },
    resetCreateDeviceState(state) {
      state.isCreating = false;
      state.createError = null;
      state.lastCreatedDevEui = null;
    },

    updateDeviceRequest(state, action: PayloadAction<UpdateDeviceActionPayload>) {
      void action.payload;
      state.isUpdating = true;
      state.updateError = null;
      state.lastUpdatedDevEui = null;
    },
    updateDeviceSuccess(state, action: PayloadAction<DeviceItem>) {
      state.isUpdating = false;
      state.lastUpdatedDevEui = action.payload.dev_eui;
      state.current = action.payload;
    },
    updateDeviceFailure(state, action: PayloadAction<string>) {
      state.isUpdating = false;
      state.updateError = action.payload;
    },
    resetUpdateDeviceState(state) {
      state.isUpdating = false;
      state.updateError = null;
      state.lastUpdatedDevEui = null;
    },

    removeDeviceRequest(state, action: PayloadAction<string>) {
      void action.payload;
      state.isRemoving = true;
      state.removeError = null;
      state.lastRemovedDevEui = null;
    },
    removeDeviceSuccess(state, action: PayloadAction<string>) {
      state.isRemoving = false;
      state.lastRemovedDevEui = action.payload;
      state.items = state.items.filter((d) => d.dev_eui !== action.payload);
    },
    removeDeviceFailure(state, action: PayloadAction<string>) {
      state.isRemoving = false;
      state.removeError = action.payload;
    },
    resetRemoveDeviceState(state) {
      state.isRemoving = false;
      state.removeError = null;
      state.lastRemovedDevEui = null;
    },

    markCalibrationRequest(state, action: PayloadAction<MarkCalibrationActionPayload>) {
      void action.payload;
      state.isCalibrating = true;
      state.calibrateError = null;
      state.lastCalibratedDevEui = null;
    },
    markCalibrationSuccess(state, action: PayloadAction<DeviceItem>) {
      state.isCalibrating = false;
      state.lastCalibratedDevEui = action.payload.dev_eui;
      state.current = action.payload;
    },
    markCalibrationFailure(state, action: PayloadAction<string>) {
      state.isCalibrating = false;
      state.calibrateError = action.payload;
    },
    resetCalibrationState(state) {
      state.isCalibrating = false;
      state.calibrateError = null;
      state.lastCalibratedDevEui = null;
    },
  },
});

export const {
  getDeviceListRequest,
  getDeviceListSuccess,
  getDeviceListFailure,
  getDeviceStatisticsRequest,
  getDeviceStatisticsSuccess,
  getDeviceStatisticsFailure,
  getDeviceRequest,
  getDeviceSuccess,
  getDeviceFailure,
  getDeviceProfilesRequest,
  getDeviceProfilesSuccess,
  getDeviceProfilesFailure,
  getDeviceMetricsRequest,
  getDeviceMetricsSuccess,
  getDeviceMetricsFailure,
  getDeviceEventsRequest,
  getDeviceEventsSuccess,
  getDeviceEventsFailure,
  createDeviceRequest,
  createDeviceSuccess,
  createDeviceFailure,
  resetCreateDeviceState,
  updateDeviceRequest,
  updateDeviceSuccess,
  updateDeviceFailure,
  resetUpdateDeviceState,
  removeDeviceRequest,
  removeDeviceSuccess,
  removeDeviceFailure,
  resetRemoveDeviceState,
  markCalibrationRequest,
  markCalibrationSuccess,
  markCalibrationFailure,
  resetCalibrationState,
} = deviceSlice.actions;

export default deviceSlice.reducer;
